import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { decodeHtmlEntities } from '../../helpers/htmlUtils';

/**
 * Quiz header
 *
 * @param {object} questionData - The question data
 *
 * @return {JSX.Element} Quiz header component
 */
export default function QuizQuestion({
  questionData,
  changeAnswer,
  changeFeedback,
  quizFinished,
  quizExpired,
  selectedAnswers,
  feedbackResponses,
  showAnswer,
}) {
  const [givenAnswer, setGivenAnswer] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const { t } = useTranslation('quiz');

  // Safely destructure with defaults
  const title = questionData?.title;
  const image = questionData?.image;
  const question = questionData?.question;
  const id = questionData?.id;
  const answers = questionData?.answers || [];
  const questionType = questionData?.type || 'single-choice';
  const feedbackRequired = questionData?.feedback_required || false;
  
  // Filter out any empty answers (defensive check)
  const validAnswers = answers?.filter(answer => answer?.text && answer.text.trim() !== '') || [];
  const hasAnswers = validAnswers.length > 0;
  
  // Check if all answers have the same points (all are "correct")
  const allAnswersSamePoints = hasAnswers && validAnswers.every(a => a.points === validAnswers[0]?.points);
  
  // If all answers have the same points, any answer is correct (use -1 as special marker)
  // Otherwise, find the answer with 1 point
  const correctAnswer = allAnswersSamePoints ? -1 : validAnswers.findIndex((obj) => obj.points === 1);

  // Track if we've already auto-marked this info page to prevent duplicate calls
  const [hasAutoMarked, setHasAutoMarked] = useState(false);

  // Reset auto-marked state when question changes
  useEffect(() => {
    setHasAutoMarked(false);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    
    const answerIndex = selectedAnswers.findIndex((obj) => obj.question === id);

    if (answerIndex >= 0) {
      setGivenAnswer(selectedAnswers[answerIndex]);
    } else if (!hasAnswers && questionType !== 'feedback' && !hasAutoMarked) {
      // Auto-mark informational questions (no answers) as viewed - only once
      setHasAutoMarked(true);
      changeAnswer(0, id, 0);
    }
    
    // Load existing feedback if any
    if (questionType === 'feedback' && feedbackResponses) {
      const feedbackIndex = feedbackResponses.findIndex((obj) => obj.question === id);
      if (feedbackIndex >= 0) {
        setFeedbackText(feedbackResponses[feedbackIndex].response || '');
      }
    }
  }, [selectedAnswers, feedbackResponses, id, hasAnswers, questionType, changeAnswer, hasAutoMarked]);

  useEffect(() => {
    // Make sure we see the explanation when we go to the previous question.
    if (givenAnswer?.answer !== undefined) {
      setSelectedIndex(givenAnswer?.answer);
    }
  }, [givenAnswer, selectedIndex]);

  // Safety check for undefined questionData
  if (!questionData) {
    return null;
  }

  return (
    <section className="quiz__question">
      <div className="col-12 col-md-10 offset-md-1">
        {image && <img className="img-fluid" src={image} alt="question" />}
      </div>
      <div className="col-12 col-md-10 offset-md-1">
        <h2 className="quiz__question--title">{decodeHtmlEntities(title)}</h2>
        <p>{decodeHtmlEntities(question)}</p>
        {questionType === 'feedback' && !quizFinished && !quizExpired && (
          <div className="col-12">
            <textarea
              className="feedback-textarea"
              placeholder={t('feedbackPlaceholder')}
              value={feedbackText}
              onChange={(e) => {
                setFeedbackText(e.target.value);
                changeFeedback(id, e.target.value, question);
              }}
              rows="6"
              required={feedbackRequired}
            />
            {feedbackRequired && !feedbackText.trim() && (
              <p className="feedback-validation-message">
                {t('feedbackRequired')}
              </p>
            )}
          </div>
        )}
        {questionType === 'feedback' && quizFinished && feedbackText && (
          <div className="col-12">
            <div className="feedback-display">
              <strong>{t('yourFeedback')}</strong>
              <p>{feedbackText}</p>
            </div>
          </div>
        )}
        {!hasAnswers && questionType !== 'feedback' && (
          <div className="col-12">
            <p className="info-message" style={{ fontStyle: 'italic', marginTop: '2rem' }}>
              {/* Informational content only - no answer required */}
            </p>
          </div>
        )}
        {hasAnswers &&
          questionType === 'single-choice' &&
          !quizFinished &&
          !quizExpired &&
          validAnswers.map((answer, i) => {
            return (
              <div key={i} className="col-12 col-md-6 offset-md-3">
                <button
                  className={`button answer
                    ${givenAnswer?.answer === i ? 'active' : ''}
                    ${showAnswer ? 'answered' : ''}
                    ${
                      showAnswer &&
                      (allAnswersSamePoints ? givenAnswer?.answer === i : correctAnswer === i) &&
                      Object.keys(givenAnswer).length !== 0
                        ? 'correct-answer'
                        : ''
                    }
                    ${
                      showAnswer &&
                      !allAnswersSamePoints &&
                      correctAnswer !== i &&
                      Object.keys(givenAnswer).length !== 0
                        ? 'wrong-answer'
                        : ''
                    }
                  `}
                  disabled={showAnswer}
                  onClick={() => {
                    // If all answers are correct (-1), pass the selected answer as the correct one
                    changeAnswer(i, id, allAnswersSamePoints ? i : correctAnswer);
                    setSelectedIndex(i);
                  }}
                >
                  {answer.text}
                </button>
              </div>
            );
          })}
        {Object.keys(givenAnswer).length !== 0 && showAnswer && (
          <div className="col-12">
            <p className="explanation">
              {questionData?.answers[selectedIndex]?.explanation}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
